// Field-level validator map for SSORequestAccountForm
// Each function returns true (valid) or an error message string.

// Simple reusable regexes
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// requires 10 digits, with accommodation for common formatting characters
// valid examples: 8005551234, 800-555-1234, (800) 555-1234
export const PHONE_REGEX = /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4}$/;

// Reusable primitive checks
const isProvided = value => typeof value === "string" && value.trim().length > 0;
const required = (value, msg) => (isProvided(value) ? true : msg);
const requiredMinLength = (value, len, msg) => (isProvided(value) && value.trim().length >= len ? true : msg);

export const validator = {
  // REQUIRES - Any value
  FirstName: value => required(value, "First Name is required"),

  // REQUIRES - Any value
  LastName: value => required(value, "Last Name is required"),

  // REQUIRES - Valid email address from stewart.com domain
  Email: value => {
    const base = required(value, "Email is required");
    if (base !== true) return base;
    return EMAIL_REGEX.test(value.trim()) ? true : "Invalid email address";
  },

  // REQUIRES - Valid phone number
  OfficePhone: value => {
    if(!value || value.trim() === "") return true; // optional
    return PHONE_REGEX.test(value.trim()) ? true : "Invalid phone number";
  },

  // REQUIRES - Valid phone number
  MobilePhone: value => {
    if(!value || value.trim() === "") return true; // optional
    return PHONE_REGEX.test(value.trim()) ? true : "Invalid phone number";
  },

// REQUIRED - Any value
  CompanyName: value => required(value, "Company Name is required"),

  // REQUIRES - Any value 
  BusinessLine: value => required(value, "You must select at least one option"),

  // REQUIRES - Any 5-digit ZIP code
  ZipCode: value => {
    const base = required(value, "ZIP Code is required");
    if (base !== true) return base;
    return /^\d{5}$/.test(value.trim()) ? true : "Please provide a 5-digit ZIP Code";
  },

  // REQUIRES - Any value
  City: value => required(value, "City/Town/Municipality is required"),

  // REQUIRES - Any value
  State: value => required(value, "State is required"),

  // Optional fields: only validate format if provided
  StewartContactEmail: value => {
    if (!isProvided(value)) return true; // optional

    // Check if email ends with stewart.com
    if (!value.trim().toLowerCase().endsWith('stewart.com')) {
      return "Email must be from stewart.com domain";
    }
    return EMAIL_REGEX.test(value.trim()) ? true : "Invalid Stewart Contact Email";
  },
};

// Fields that are omitted from the UI (and therefore validation) per site variant.
// Extend this map when new variants need field-level opt-outs.
export const HIDDEN_FIELDS_BY_VARIANT = {
  stewartcom: new Set([
    "CompanyName",
    "BusinessLine",
    "StewartContact",
    "StewartContactEmail",
    "NewsLetterSignUp",
  ]),
};

export function isFieldHidden(field, variant) {
  const set = HIDDEN_FIELDS_BY_VARIANT[variant];
  return !!(set && set.has(field));
}

// Validate an entire data object, returning { valid, errors }
export function validateData(data, mode = "register", variant = "") {
  const errors = {};
  Object.keys(validator).forEach(field => {
    // Skip fields hidden for the current site variant
    if (isFieldHidden(field, variant)) {
      return true;
    }
    // Skip BusinessLine validation in edit mode
    if(field === "BusinessLine" && mode === "edit") {
      return true;
    }
    const result = validator[field](data[field]);
    if (result !== true) {
      errors[field] = result;
    }
  });

  return { valid: Object.keys(errors).length === 0, errors };
}

// Convenience single-field validator (if needed elsewhere)
export function validateField(name, value) {
  if (!validator[name]) return true; // Unknown field treated as valid
  return validator[name](value);
}
